/**
 * Schalter für die Zahlungsdialoge V2 („Modern Premium C + Touch-Ziffernblock aus B“, Figma
 * Section 275:2598). **Vorgabe AUS** – ohne Schalter zeigt die Kasse die bisherigen Dialoge
 * unverändert. Die visuelle Abnahme liegt beim Nutzer.
 *
 * Bis zur Abnahme ist der Schalter beschränkt (Codex-Prüfung ZD-V2, M2): Er wirkt nur
 * - im Entwicklungsbetrieb (`import.meta.env.DEV`, `npm run kasse:dev`) oder
 * - an einer Testkasse aus `ZAHLUNGSDIALOG_V2_TESTKASSEN` (Vorgabe: die isolierte Testkasse
 *   `UI-V2-1`).
 * Überall sonst werden Adressparameter und gespeicherter Wert ignoriert (und nichts gemerkt).
 *
 * Einschalten (nur dort):
 * - Adresse einmal mit `?zahlungsdialogV2=1` öffnen (wird gemerkt), `?zahlungsdialogV2=0`
 *   schaltet wieder aus; oder
 * - in den Entwicklerwerkzeugen `localStorage.setItem('bilanx.zahlungsdialogV2', '1')`.
 *
 * Gelesen wird beim Öffnen eines Zahlungsdialogs; ein laufender Dialog wechselt nie die Ansicht.
 * Der Schalter ändert nur die Darstellung – Ablauf, Zahlungs-API und Sicherungen bleiben gleich.
 */
export const ZAHLUNGSDIALOG_V2_SCHLUESSEL = 'bilanx.zahlungsdialogV2';
/** Name des Adressparameters (`?zahlungsdialogV2=1` bzw. `=0`). */
export const ZAHLUNGSDIALOG_V2_PARAMETER = 'zahlungsdialogV2';
/** Kassen-IDs, an denen der Schalter außerhalb des Entwicklungsbetriebs wirkt (bis zur Abnahme). */
export const ZAHLUNGSDIALOG_V2_TESTKASSEN: readonly string[] = ['UI-V2-1'];

export interface SchalterSpeicher {
  getItem(schluessel: string): string | null;
  setItem(schluessel: string, wert: string): void;
  removeItem(schluessel: string): void;
}

export interface SchalterUmgebung {
  suche?: string;
  speicher?: SchalterSpeicher | null;
  /** Entwicklungsbetrieb; Vorgabe `import.meta.env.DEV`. */
  entwicklung?: boolean;
}

function standardSuche(): string {
  try {
    return typeof window === 'undefined' ? '' : window.location.search;
  } catch {
    return '';
  }
}

function standardSpeicher(): SchalterSpeicher | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** Darf der Schalter hier überhaupt wirken? (Entwicklungsbetrieb oder Testkasse) */
export function zahlungsdialogV2Erlaubt(
  kassenId: string | null | undefined,
  entwicklung: boolean = import.meta.env.DEV,
): boolean {
  return (
    entwicklung || (typeof kassenId === 'string' && ZAHLUNGSDIALOG_V2_TESTKASSEN.includes(kassenId))
  );
}

/**
 * Sind die Zahlungsdialoge V2 an dieser Kasse eingeschaltet? Nur wenn erlaubt (siehe oben) und
 * der Wert `'1'` gesetzt ist; jeder Fehler (Speicher gesperrt, kaputter Wert) ergibt AUS.
 */
export function zahlungsdialogV2Aktiv(
  kassenId: string | null | undefined,
  umgebung: SchalterUmgebung = {},
): boolean {
  if (!zahlungsdialogV2Erlaubt(kassenId, umgebung.entwicklung ?? import.meta.env.DEV)) {
    return false;
  }
  const suche = umgebung.suche ?? standardSuche();
  const speicher = umgebung.speicher === undefined ? standardSpeicher() : umgebung.speicher;
  let parameter: string | null;
  try {
    parameter = new URLSearchParams(suche).get(ZAHLUNGSDIALOG_V2_PARAMETER);
  } catch {
    parameter = null;
  }
  if (parameter === '1' || parameter === '0') {
    try {
      if (parameter === '1') {
        speicher?.setItem(ZAHLUNGSDIALOG_V2_SCHLUESSEL, '1');
      } else {
        speicher?.removeItem(ZAHLUNGSDIALOG_V2_SCHLUESSEL);
      }
    } catch {
      // nicht merkbar (privater Modus) – gilt dann nur für diese Adresse
    }
    return parameter === '1';
  }
  try {
    return speicher?.getItem(ZAHLUNGSDIALOG_V2_SCHLUESSEL) === '1';
  } catch {
    return false;
  }
}
